import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Globe, Phone, Mail, ShieldCheck, LogIn } from "lucide-react";
import { cn } from "../lib/utils";
import { brandPhoneLink } from "../lib/whatsapp";
import MobileMenuDrawer from "./MobileMenuDrawer";

const navItems = [
  { key: "home", to: "/" },
  { key: "plans", to: "/plans" },
  { key: "how", to: "/how-it-works" },
  { key: "faq", to: "/faq" },
  { key: "about", to: "/about" },
  { key: "contact", to: "/contact" },
];

const PUCT_REP = "PUCT REP# Pending";
const HEADER_PHONE = "(800) 715-6516";

export default function Navbar({ ribbon = true }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Reset admin dark-mode theme attributes when entering public pages.
  // Without this, navigating /admin → / would keep `data-admin-theme="dark"`
  // active and the dark CSS overrides would bleed into the marketing site.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-admin-theme", "light");
    root.classList.remove("dark");
  }, []);

  const switchLang = () => {
    const next = i18n.language?.startsWith("es") ? "en" : "es";
    i18n.changeLanguage(next);
    document.documentElement.lang = next;
  };

  const isActive = (to) => location.pathname === to;

  return (
    <>
      {/* TopRibbon removed (Feb 2026) — user feedback: the "Energía confiable…"
          dark green/navy strip at the very top of every page felt cluttered
          and competed with the hero. The same info (phone + email) lives in
          the main navbar and footer. Keeping the `ribbon` prop intact for
          backwards-compat with Layout consumers; it's now a no-op. */}

      <header
        className={cn(
          "sticky top-0 z-40 px-4 pt-3 md:pt-4 transition-all duration-300",
          scrolled ? "pb-2" : "pb-0"
        )}
        data-testid="header-sticky"
      >
        <div
          className={cn(
            "max-w-[1180px] mx-auto rounded-[28px] md:rounded-full flex flex-wrap md:flex-nowrap items-center justify-between gap-3 px-4 md:px-5 py-2.5 transition-all duration-300",
            scrolled
              ? "bg-white/95 backdrop-blur-md shadow-[0_8px_30px_rgba(15,23,42,0.10)] border border-slate-200"
              : "glass shadow-[0_18px_50px_rgba(15,23,42,0.06)]"
          )}
          data-testid="topbar"
        >
          <Link to="/" className="flex items-center gap-2.5 pl-1.5 group" data-testid="brand-link">
            <img
              src="/velcor-logo.png"
              alt="Velcor Energy logo"
              width={44}
              height={44}
              className="object-contain shrink-0 group-hover:scale-105 transition-transform"
              style={{ width: 44, height: 44 }}
            />
            <span className="font-black text-velcor-ink tracking-tight text-[14px] md:text-[15px]">
              Velcor Energy
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="nav-desktop">
            {navItems.map((it) => (
              <Link
                key={it.key}
                to={it.to}
                data-testid={`nav-${it.key}`}
                className={cn(
                  "pill px-3.5 h-9 text-[13px] font-extrabold transition-colors whitespace-nowrap",
                  isActive(it.to)
                    ? "bg-velcor-green/10 text-velcor-green"
                    : "text-slate-700 hover:text-velcor-green hover:bg-velcor-green/8"
                )}
              >
                {t(`nav.${it.key}`)}
              </Link>
            ))}

            <span className="w-px h-5 bg-slate-200 mx-1.5" aria-hidden />

            <a
              href={brandPhoneLink()}
              className="hidden lg:inline-flex pill px-3 h-9 text-[12.5px] font-extrabold text-velcor-ink hover:bg-velcor-green/8 hover:text-velcor-green transition-colors"
              data-testid="header-phone"
              title={HEADER_PHONE}
            >
              <Phone className="w-3.5 h-3.5 mr-1.5" />
              {HEADER_PHONE}
            </a>

            <Link
              to="/portal/login"
              className="pill px-3 h-9 text-[12.5px] font-extrabold border border-slate-200 hover:border-velcor-green hover:text-velcor-green text-slate-700 transition-colors"
              data-testid="nav-signin"
            >
              <LogIn className="w-3.5 h-3.5 mr-1.5" />
              {t("nav.signin")}
            </Link>

            <button
              onClick={switchLang}
              className="pill px-2.5 h-9 text-[12px] font-extrabold border border-slate-200 hover:border-velcor-green hover:text-velcor-green transition-colors"
              data-testid="lang-switcher"
              aria-label="Cambiar idioma"
            >
              <Globe className="w-3.5 h-3.5 mr-1" />
              {i18n.language?.startsWith("es") ? "EN" : "ES"}
            </button>
          </nav>

          <button
            className="md:hidden pill border border-slate-200 w-10 h-10 text-velcor-ink"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="nav-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Shared full-screen mobile drawer */}
      <MobileMenuDrawer open={open} onClose={() => setOpen(false)} testidPrefix="nav-mobile" />
    </>
  );
}
